import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, Ban, Calendar, Check, Copy, Crown, Globe, History, Library, Loader2, Mail, Plus, RefreshCw, Trash2, UserPlus, Users,
} from 'lucide-react';
import { api } from '../../services/api';
import { WelcomeEditor } from '../session-hub/WelcomeEditor';
import { LibraryEditor } from '../session-hub/LibraryEditor';

interface Props {
  projectId: string;
  token: string;
}

interface Hub {
  hubId: string;
  title: string;
  kind: 'library' | 'pack';
  status: 'active' | 'revoked';
  expiresAt: string;
  clientDomains: string[];
  createdAt: string;
  welcomeMessage: string | null;
  welcomeVideo: { sizeBytes: number; hasCaptions: boolean; uploadedAt: string } | null;
}

interface Visitor {
  email: string;
  displayName: string | null;
  isSponsor?: boolean;
  status: 'active' | 'removed';
  outsideClientDomains: boolean;
  addedAt: string;
  lastAccessAt: string | null;
}

interface AuditEvent {
  event: string;
  at: string;
  visitor: string | null;
  actorUserId?: string;
  deniedBy?: string[];
}

const EVENT_LABELS: Record<string, string> = {
  hub_created: 'Hub created', hub_updated: 'Hub edited', hub_revoked: 'Hub closed', link_rotated: 'Link replaced',
  item_published: 'Item shared', item_unpublished: 'Item unshared', item_viewed: 'Item opened', item_downloaded: 'Item downloaded',
  welcome_video_set: 'Welcome video set', welcome_video_removed: 'Welcome video removed', welcome_viewed: 'Welcome video watched',
  visitor_added: 'Visitor added', visitor_updated: 'Visitor edited', visitor_removed: 'Visitor removed',
  code_sent: 'Code sent', code_refused: 'Code refused', code_send_failed: 'Code email failed',
  verify_ok: 'Signed in', verify_fail: 'Wrong or expired code', denied: 'Access denied',
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const fmtTime = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const dateInput = (iso: string) => iso.slice(0, 10);
const plusDays = (days: number) => new Date(Date.now() + days * 86400_000).toISOString().slice(0, 10);

const input = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';
const primaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60';
const secondaryBtn = 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60';
const label = 'block text-sm font-semibold text-navy mb-2 flex items-center gap-1.5';

class SetupError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function call<T>(token: string, path: string, method = 'GET', data?: unknown): Promise<T> {
  try {
    const res = await api.request({ url: `/api${path}`, method, data, headers: { Authorization: `Bearer ${token}` } });
    return res.data as T;
  } catch (e: any) {
    throw new SetupError(e?.response?.data?.error ?? e?.message ?? 'Request failed', e?.response?.status ?? 0);
  }
}

const parseDomains = (raw: string) => raw.split(/[\s,]+/).map((d) => d.trim().replace(/^@/, '').toLowerCase()).filter(Boolean);

function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="px-5 py-4 border-b border-[#e2e8f0] bg-red-50 flex gap-3">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
      <p className="text-sm font-medium text-red-700">{message}</p>
    </div>
  );
}

function LinkPanel({ link, onDone }: { link: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5 flex flex-col gap-3">
      <p className="text-sm font-semibold text-navy">Copy this link now. It is shown once and cannot be shown again.</p>
      <div className="flex gap-2">
        <input readOnly value={link} className={`${input} font-mono text-xs bg-white`} onFocus={(e) => e.target.select()} />
        <button
          type="button" className={primaryBtn}
          onClick={() => { navigator.clipboard.writeText(link); setCopied(true); }}
        >
          {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Send it to the people you add below. They also need a code we email them, so the link alone does not let anyone in.
        If it leaks, use Replace link.
      </p>
      <div><button type="button" className={secondaryBtn} onClick={onDone}>Done</button></div>
    </div>
  );
}

function CreateHubForm({ projectId, token, onCreated, onCancel }: {
  projectId: string; token: string; onCreated: (hub: Hub, link: string) => void; onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [expiresOn, setExpiresOn] = useState(plusDays(30));
  const [domains, setDomains] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await call<{ hub: Hub; link: string }>(token, `/projects/${projectId}/session-hubs`, 'POST', {
        title, expiresOn, clientDomains: parseDomains(domains),
      });
      onCreated(r.hub, r.link);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <ErrorBanner message={error} />
      <div className="p-5 border-b border-[#e2e8f0]">
        <label className={label}><Library className="w-4 h-4 text-slate-400" />Title the client sees</label>
        <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Business case discovery: outputs" autoFocus />
      </div>
      <div className="p-5 border-b border-[#e2e8f0] grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={label}><Calendar className="w-4 h-4 text-slate-400" />Open until</label>
          <input type="date" className={input} value={expiresOn} min={plusDays(1)} max={plusDays(365)} onChange={(e) => setExpiresOn(e.target.value)} />
          <p className="mt-2 text-xs text-slate-400">Closes at the end of that day. Up to a year.</p>
        </div>
        <div>
          <label className={label}><Globe className="w-4 h-4 text-slate-400" />Client email domains</label>
          <input className={input} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="client.gov.uk, partner.com" />
          <p className="mt-2 text-xs text-slate-400">Codes are only emailed to these domains, unless you confirm an address yourself.</p>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
        <button type="button" className={secondaryBtn} onClick={onCancel}>Cancel</button>
        <button type="submit" className={primaryBtn} disabled={busy || !title.trim()}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create hub
        </button>
      </div>
    </form>
  );
}

function HubDetail({ projectId, token, hub, onChanged }: {
  projectId: string; token: string; hub: Hub; onChanged: (hub: Hub) => void;
}) {
  const base = `/projects/${projectId}/session-hubs/${hub.hubId}`;
  const [visitors, setVisitors] = useState<Visitor[] | null>(null);
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [confirmOutside, setConfirmOutside] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(hub.title);
  const [expiresOn, setExpiresOn] = useState(dateInput(hub.expiresAt));
  const [domains, setDomains] = useState(hub.clientDomains.join(', '));

  const hubCall = useCallback(
    <T,>(path: string, method = 'GET', data?: unknown) => call<T>(token, `${base}${path}`, method, data),
    [token, base]
  );

  const loadVisitors = useCallback(async () => {
    try {
      setVisitors((await call<{ visitors: Visitor[] }>(token, `${base}/visitors`)).visitors);
    } catch (err: any) {
      setError(err.message);
    }
  }, [token, base]);

  const loadAudit = useCallback(async () => {
    try {
      setEvents((await call<{ events: AuditEvent[] }>(token, `${base}/audit`)).events);
    } catch (err: any) {
      setError(err.message);
    }
  }, [token, base]);

  useEffect(() => { loadVisitors(); }, [loadVisitors]);
  useEffect(() => { if (showAudit) loadAudit(); }, [showAudit, loadAudit]);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const addVisitor = (e: React.FormEvent, allowOutsideDomains = false) => {
    e.preventDefault();
    return run(async () => {
      try {
        await call(token, `${base}/visitors`, 'POST', { email, displayName: name || undefined, allowOutsideDomains });
      } catch (err: any) {
        if (err.status === 422) {
          setConfirmOutside(err.message);
          return;
        }
        throw err;
      }
      setEmail('');
      setName('');
      setConfirmOutside(null);
      await loadVisitors();
      if (showAudit) await loadAudit();
    });
  };

  const removeVisitor = (v: Visitor) => run(async () => {
    await call(token, `${base}/visitors/${encodeURIComponent(v.email)}`, 'DELETE');
    await loadVisitors();
  });

  const toggleSponsor = (v: Visitor) => run(async () => {
    await call(token, `${base}/sponsor`, 'PUT', { email: v.isSponsor ? null : v.email });
    await loadVisitors();
  });

  const saveEdit = () => run(async () => {
    const r = await call<{ hub: Hub }>(token, base, 'PATCH', { title, expiresOn, clientDomains: parseDomains(domains) });
    onChanged(r.hub);
    setEditing(false);
  });

  const setStatus = (status: 'active' | 'revoked') => run(async () => {
    const r = await call<{ hub: Hub }>(token, base, 'PATCH', { status });
    onChanged(r.hub);
  });

  const rotate = () => run(async () => {
    setLink((await call<{ link: string }>(token, `${base}/rotate-link`, 'POST')).link);
  });

  const closed = hub.status === 'revoked' || Date.parse(hub.expiresAt) <= Date.now();

  return (
    <div className="flex flex-col gap-6">
      {link && <LinkPanel link={link} onDone={() => setLink(null)} />}

      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <ErrorBanner message={error} />
        <div className="p-5 border-b border-[#e2e8f0] flex flex-wrap items-start justify-between gap-4">
          {editing ? (
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <label className={label}>Title</label>
                <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
              </div>
              <div>
                <label className={label}>Open until</label>
                <input type="date" className={input} value={expiresOn} min={plusDays(1)} max={plusDays(365)} onChange={(e) => setExpiresOn(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className={label}>Client email domains</label>
                <input className={input} value={domains} onChange={(e) => setDomains(e.target.value)} />
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-navy">{hub.title}</h3>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${closed ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-green-50 text-green-600 border-green-200'}`}>
                  {hub.status === 'revoked' ? 'Closed' : closed ? 'Expired' : 'Open'}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Open until {fmtDate(hub.expiresAt)} · {hub.clientDomains.length ? hub.clientDomains.join(', ') : 'no client domains yet'}
              </p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <button type="button" className={secondaryBtn} onClick={() => setEditing(false)}>Cancel</button>
                <button type="button" className={primaryBtn} disabled={busy} onClick={saveEdit}><Check size={16} /> Save</button>
              </>
            ) : (
              <>
                <button type="button" className={secondaryBtn} onClick={() => setEditing(true)}>Edit</button>
                <button type="button" className={secondaryBtn} disabled={busy} onClick={rotate} title="Old links stop working at once">
                  <RefreshCw size={16} /> Replace link
                </button>
                {hub.status === 'active' ? (
                  <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setStatus('revoked')}><Ban size={16} /> Close hub</button>
                ) : (
                  <button type="button" className={secondaryBtn} disabled={busy} onClick={() => setStatus('active')}>Reopen hub</button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="p-5 border-b border-[#e2e8f0]">
          <h4 className="text-[11px] font-bold text-primary tracking-widest uppercase mb-3 flex items-center gap-1.5"><Users size={14} /> Who can sign in</h4>
          <form onSubmit={(e) => addVisitor(e)} className="grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_auto] gap-3">
            <input className={input} type="email" value={email} onChange={(e) => { setEmail(e.target.value); setConfirmOutside(null); }} placeholder="name@client.gov.uk" />
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" maxLength={120} />
            <button type="submit" className={primaryBtn} disabled={busy || !email.trim()}><UserPlus size={16} /> Add</button>
          </form>
          {confirmOutside && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-amber-800">{confirmOutside}</p>
              <button type="button" className={secondaryBtn} disabled={busy} onClick={(e) => addVisitor(e as any, true)}>Allow this address</button>
            </div>
          )}
        </div>

        {visitors === null ? (
          <div className="flex justify-center items-center py-10 text-[#64748b]"><Loader2 size={20} className="animate-spin" /></div>
        ) : visitors.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-400 text-center">No one added yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50 text-left text-xs text-slate-500">
                  <th className="px-5 py-2.5 font-semibold">Email</th>
                  <th className="px-5 py-2.5 font-semibold">Name</th>
                  <th className="px-5 py-2.5 font-semibold">Last signed in</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {visitors.map((v) => (
                  <tr key={v.email} className={v.status === 'removed' ? 'text-slate-400' : 'text-slate-600'}>
                    <td className="px-5 py-3">
                      <span className={v.status === 'removed' ? 'line-through' : 'text-navy font-medium'}>{v.email}</span>
                      {v.isSponsor && <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Sponsor</span>}
                      {v.outsideClientDomains && <span className="ml-2 text-xs text-amber-700">outside client domains</span>}
                    </td>
                    <td className="px-5 py-3">{v.displayName ?? ''}</td>
                    <td className="px-5 py-3">{v.status === 'removed' ? 'Removed' : v.lastAccessAt ? fmtTime(v.lastAccessAt) : 'Not yet'}</td>
                    <td className="px-5 py-3 text-right">
                      {v.status === 'active' && (
                        <button type="button" className="mr-3 text-slate-400 hover:text-amber-700" title={v.isSponsor ? 'Remove the sponsor tag' : 'Make sponsor (one per hub)'} disabled={busy} onClick={() => toggleSponsor(v)}>
                          <Crown size={16} />
                        </button>
                      )}
                      {v.status === 'active' && (
                        <button type="button" className="text-slate-400 hover:text-red-600" title="Remove" disabled={busy} onClick={() => removeVisitor(v)}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {hub.kind === 'library' && <LibraryEditor call={hubCall} onChanged={() => { if (showAudit) loadAudit(); }} />}

      <WelcomeEditor hub={hub} call={hubCall} onChanged={onChanged} />

      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        <button type="button" onClick={() => setShowAudit(!showAudit)} className="w-full px-5 py-4 flex items-center justify-between text-left">
          <span className="text-[11px] font-bold text-primary tracking-widest uppercase flex items-center gap-1.5"><History size={14} /> Activity</span>
          <span className="text-xs text-slate-400">{showAudit ? 'Hide' : 'Show'}</span>
        </button>
        {showAudit && (
          events === null ? (
            <div className="flex justify-center items-center py-8 text-[#64748b]"><Loader2 size={20} className="animate-spin" /></div>
          ) : events.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-400">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-[#e2e8f0] border-t border-[#e2e8f0]">
              {events.map((e, i) => (
                <li key={`${e.at}-${i}`} className="px-5 py-2.5 text-sm flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-slate-400 w-36 flex-shrink-0">{fmtTime(e.at)}</span>
                  <span className="text-navy font-medium">{EVENT_LABELS[e.event] ?? e.event}</span>
                  {e.visitor && <span className="text-slate-500">{e.visitor}</span>}
                  {e.deniedBy?.length ? <span className="text-xs text-slate-400">({e.deniedBy.join(', ')})</span> : null}
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}

export function SessionHubTab({ projectId, token }: Props) {
  const [hubs, setHubs] = useState<Hub[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await call<{ hubs: Hub[] }>(token, `/projects/${projectId}/session-hubs`);
      setHubs(r.hubs);
      setSelected((cur) => cur ?? r.hubs[0]?.hubId ?? null);
    } catch (err: any) {
      setError(err.status === 403
        ? 'You need the Session Hub setup permission and write access to this project.'
        : err.message);
      setHubs([]);
    }
  }, [projectId, token]);

  useEffect(() => { load(); }, [load]);

  const hub = hubs?.find((h) => h.hubId === selected) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          A private, time-limited page where named client contacts sign in with an emailed code to watch your welcome and open the deliverables you share.
        </p>
        {!creating && (
          <button type="button" className={primaryBtn} onClick={() => setCreating(true)}><Plus size={16} /> New hub</button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}

      {newLink && <LinkPanel link={newLink} onDone={() => setNewLink(null)} />}

      {creating && (
        <CreateHubForm
          projectId={projectId} token={token} onCancel={() => setCreating(false)}
          onCreated={(h, link) => {
            setCreating(false);
            setNewLink(link);
            setHubs((cur) => [h, ...(cur ?? [])]);
            setSelected(h.hubId);
          }}
        />
      )}

      {hubs === null ? (
        <div className="flex justify-center items-center py-20 text-[#64748b]"><Loader2 size={22} className="animate-spin" /></div>
      ) : hubs.length === 0 ? (
        !creating && !error && (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Mail size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No Session Hub for this project yet.</p>
            <p className="text-slate-400 text-xs mt-2">Create one to give named client contacts a private, time-limited page.</p>
          </div>
        )
      ) : (
        <>
          {hubs.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {hubs.map((h) => (
                <button
                  key={h.hubId} type="button" onClick={() => setSelected(h.hubId)}
                  className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${selected === h.hubId ? 'border-primary bg-primary/5 text-navy' : 'border-[#e2e8f0] text-slate-600 hover:border-primary/40'}`}
                >
                  {h.title}
                </button>
              ))}
            </div>
          )}
          {hub && (
            <HubDetail
              key={hub.hubId} projectId={projectId} token={token} hub={hub}
              onChanged={(next) => setHubs((cur) => (cur ?? []).map((h) => (h.hubId === next.hubId ? { ...h, ...next } : h)))}
            />
          )}
        </>
      )}
    </div>
  );
}
